/**
 * Design Tokens — aligned with Uber's Base Web design system.
 * Trio brand colors are preserved; all dimensions, typography, motion,
 * shadows, borders, and radii match Base Web token values.
 *
 * This file is the single source of truth. theme.ts imports from here.
 */

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------
export const typography = {
  font100:  { fontSize: '12px', fontWeight: 400, lineHeight: '20px' },
  font150:  { fontSize: '12px', fontWeight: 500, lineHeight: '16px' },
  font200:  { fontSize: '14px', fontWeight: 400, lineHeight: '20px' },
  font250:  { fontSize: '14px', fontWeight: 500, lineHeight: '16px' },
  font300:  { fontSize: '16px', fontWeight: 400, lineHeight: '24px' },
  font350:  { fontSize: '16px', fontWeight: 500, lineHeight: '20px' },
  font400:  { fontSize: '18px', fontWeight: 400, lineHeight: '28px' },
  font450:  { fontSize: '18px', fontWeight: 500, lineHeight: '24px' },
  font550:  { fontSize: '20px', fontWeight: 700, lineHeight: '28px' },
  font650:  { fontSize: '24px', fontWeight: 700, lineHeight: '32px' },
  font750:  { fontSize: '28px', fontWeight: 700, lineHeight: '36px' },
  font850:  { fontSize: '32px', fontWeight: 700, lineHeight: '40px' },
  font950:  { fontSize: '36px', fontWeight: 700, lineHeight: '44px' },
  font1050: { fontSize: '40px', fontWeight: 700, lineHeight: '52px' },
  font1350: { fontSize: '52px', fontWeight: 700, lineHeight: '64px' },
  font1450: { fontSize: '96px', fontWeight: 700, lineHeight: '112px' },

  // Semantic aliases
  ParagraphXSmall: { fontSize: '12px', fontWeight: 400, lineHeight: '20px' },
  ParagraphSmall:  { fontSize: '14px', fontWeight: 400, lineHeight: '20px' },
  ParagraphMedium: { fontSize: '16px', fontWeight: 400, lineHeight: '24px' },
  ParagraphLarge:  { fontSize: '18px', fontWeight: 400, lineHeight: '28px' },
  LabelXSmall:     { fontSize: '12px', fontWeight: 500, lineHeight: '16px' },
  LabelSmall:      { fontSize: '14px', fontWeight: 500, lineHeight: '16px' },
  LabelMedium:     { fontSize: '16px', fontWeight: 500, lineHeight: '20px' },
  LabelLarge:      { fontSize: '18px', fontWeight: 500, lineHeight: '24px' },
  HeadingXSmall:   { fontSize: '20px', fontWeight: 700, lineHeight: '28px' },
  HeadingSmall:    { fontSize: '24px', fontWeight: 700, lineHeight: '32px' },
  HeadingMedium:   { fontSize: '28px', fontWeight: 700, lineHeight: '36px' },
  HeadingLarge:    { fontSize: '32px', fontWeight: 700, lineHeight: '40px' },
  HeadingXLarge:   { fontSize: '36px', fontWeight: 700, lineHeight: '44px' },
  HeadingXXLarge:  { fontSize: '40px', fontWeight: 700, lineHeight: '52px' },
  DisplayMedium:   { fontSize: '52px', fontWeight: 700, lineHeight: '64px' },
  DisplayLarge:    { fontSize: '96px', fontWeight: 700, lineHeight: '112px' },
} as const;

// ---------------------------------------------------------------------------
// Spacing (Base Web sizing scale)
// ---------------------------------------------------------------------------
export const sizing = {
  scale0:    '2px',
  scale100:  '4px',
  scale200:  '6px',
  scale300:  '8px',
  scale400:  '10px',
  scale500:  '12px',
  scale550:  '14px',
  scale600:  '16px',
  scale650:  '18px',
  scale700:  '20px',
  scale750:  '22px',
  scale800:  '24px',
  scale850:  '28px',
  scale900:  '32px',
  scale950:  '36px',
  scale1000: '40px',
  scale1200: '48px',
  scale1400: '56px',
  scale1600: '64px',
  scale2400: '96px',
  scale3200: '128px',
  scale4800: '192px',
} as const;

// ---------------------------------------------------------------------------
// Border Radius
// ---------------------------------------------------------------------------
export const radii = {
  radius100: '2px',
  radius200: '4px',
  radius300: '8px',
  radius400: '12px',
  radius500: '16px',
  pill: '999px',

  // Component-specific
  buttonBorderRadius: '8px',
  inputBorderRadius: '8px',
  tagBorderRadius: '24px',
  popoverBorderRadius: '8px',
  surfaceBorderRadius: '0px',

  // Backward compat aliases
  sm: '8px',
  md: '12px',
  lg: '16px',
} as const;

// ---------------------------------------------------------------------------
// Shadows (Elevation)
// ---------------------------------------------------------------------------
export const shadows = {
  shadow400: '0 1px 4px hsla(0, 0%, 0%, 0.16)',
  shadow500: '0 2px 8px hsla(0, 0%, 0%, 0.16)',
  shadow600: '0 4px 16px hsla(0, 0%, 0%, 0.16)',
  shadow700: '0 8px 24px hsla(0, 0%, 0%, 0.16)',

  shallowAbove: '0px -4px 16px rgba(0, 0, 0, 0.12)',
  shallowBelow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
  deepAbove: '0px -16px 48px rgba(0, 0, 0, 0.22)',
  deepBelow: '0px 16px 48px rgba(0, 0, 0, 0.22)',

  // Backward compat aliases
  sm: '0 1px 4px hsla(0, 0%, 0%, 0.16)',
  md: '0 4px 16px hsla(0, 0%, 0%, 0.16)',
  lg: '0 8px 24px hsla(0, 0%, 0%, 0.16)',
} as const;

// ---------------------------------------------------------------------------
// Borders (opacity-based like Base Web)
// ---------------------------------------------------------------------------
export const borders = {
  border100: '1px solid hsla(0, 0%, 0%, 0.04)',
  border200: '1px solid hsla(0, 0%, 0%, 0.08)',
  border300: '1px solid hsla(0, 0%, 0%, 0.12)',
  border400: '1px solid hsla(0, 0%, 0%, 0.16)',
  border500: '1px solid hsla(0, 0%, 0%, 0.20)',
  border600: '1px solid hsla(0, 0%, 0%, 0.24)',

  // Parsed values for styled-components that need just the color
  borderColor100: 'hsla(0, 0%, 0%, 0.04)',
  borderColor200: 'hsla(0, 0%, 0%, 0.08)',
  borderColor300: 'hsla(0, 0%, 0%, 0.12)',
  borderColor400: 'hsla(0, 0%, 0%, 0.16)',
  borderColor500: 'hsla(0, 0%, 0%, 0.20)',
  borderColor600: 'hsla(0, 0%, 0%, 0.24)',
} as const;

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------
export const motion = {
  duration100: '100ms',
  duration200: '200ms',
  duration300: '300ms',
  duration400: '400ms',
  duration500: '500ms',

  easeDefault: 'cubic-bezier(0.2, 0.8, 0.4, 1)',
  easeIn: 'cubic-bezier(0.8, 0.2, 0.6, 1)',
  easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// ---------------------------------------------------------------------------
// Semantic Colors (Trio brand palette → Base Web naming)
// ---------------------------------------------------------------------------
export const semanticColors = {
  // Backgrounds
  backgroundPrimary: '#FFFFFF',
  backgroundSecondary: '#F4F4F5',
  backgroundTertiary: '#E4E4E7',
  backgroundInversePrimary: '#09090B',
  backgroundStateDisabled: '#E4E4E7',
  backgroundNegative: '#FF5E54',
  backgroundPositive: '#2AD062',
  backgroundWarning: '#FFD166',
  backgroundAccent: '#054AD9',

  // Content
  contentPrimary: '#09090B',
  contentSecondary: '#4A4B4E',
  contentTertiary: '#71717A',
  contentInversePrimary: '#FFFFFF',
  contentStateDisabled: '#C5C5C7',
  contentNegative: '#FF5E54',
  contentPositive: '#2AD062',
  contentWarning: '#FFD166',
  contentAccent: '#054AD9',

  // Borders
  borderOpaque: '#F4F4F5',
  borderTransparent: 'hsla(0, 0%, 0%, 0.08)',
  borderSelected: '#054AD9',
  borderNegative: '#FF5E54',
  borderPositive: '#2AD062',

  // Input
  inputFill: '#F4F4F5',
  inputFillActive: '#FFFFFF',
  inputFillError: '#FFFFFF',
  inputFillDisabled: '#E4E4E7',
  inputBorder: '#F4F4F5',
  inputPlaceholder: '#71717A',
  inputPlaceholderDisabled: '#C5C5C7',

  // Button
  buttonPrimaryFill: '#054AD9',
  buttonPrimaryText: '#FFFFFF',
  buttonSecondaryFill: '#F4F4F5',
  buttonSecondaryText: '#09090B',
  buttonTertiaryFill: 'transparent',
  buttonTertiaryText: '#09090B',
  buttonDisabledFill: '#E4E4E7',
  buttonDisabledText: '#C5C5C7',
} as const;

// ---------------------------------------------------------------------------
// Component sizing (Base Web button/input/tag dimensions)
// ---------------------------------------------------------------------------
export const componentSizing = {
  button: {
    mini:    { height: '28px', fontSize: '12px', lineHeight: '16px', fontWeight: 500, paddingV: '6px',  paddingH: '8px'  },
    compact: { height: '36px', fontSize: '14px', lineHeight: '16px', fontWeight: 500, paddingV: '10px', paddingH: '12px' },
    default: { height: '48px', fontSize: '16px', lineHeight: '20px', fontWeight: 500, paddingV: '14px', paddingH: '16px' },
    large:   { height: '56px', fontSize: '18px', lineHeight: '24px', fontWeight: 500, paddingV: '16px', paddingH: '20px' },
  },
  input: {
    default: { height: '48px', paddingV: '10px', paddingH: '14px', borderWidth: '2px', borderRadius: '8px' },
  },
  tag: {
    xSmall: { height: '20px', paddingV: '2px', paddingH: '4px',  borderRadius: '4px',  fontSize: '12px' },
    small:  { height: '24px', paddingV: '4px', paddingH: '6px',  borderRadius: '4px',  fontSize: '12px' },
    medium: { height: '32px', paddingV: '4px', paddingH: '8px',  borderRadius: '8px',  fontSize: '14px' },
    large:  { height: '40px', paddingV: '8px', paddingH: '10px', borderRadius: '8px',  fontSize: '16px' },
  },
  checkbox: {
    size: '16px',
    borderRadius: '2px',
  },
} as const;
